/**
 * batchAllocation.ts — FEFO (First-Expire-First-Out) batch allocation.
 *
 * Faithful port of `allocateBatchesFEFO` from
 * `BigxBookApp/src/inventory/InventoryService.ts`. The two clients MUST agree:
 * batch quantities are event-derived on the server from batch-tagged
 * `stock_movements` (see `backend-api/src/sync/stockDerivation.ts`), so if one
 * client allocates differently — or, as desktop previously did, not at all —
 * the per-batch projection diverges from reality on every device.
 *
 * Desktop used to write `batchId = null` on every sale movement. The server's
 * `batchStockDelta` fold ignores untagged movements, so a batch-tracked product
 * sold on desktop never decremented its batch. Mobile's FEFO would then allocate
 * from a batch holding no real stock — a silent oversell.
 */

export interface BatchInfo {
  id: string;
  expiryDate: Date | null;
  quantity: number;
}

export interface BatchAllocation {
  batchId: string;
  allocatedQty: number;
}

/**
 * Allocate `requiredQty` across `availableBatches`, soonest-to-expire first.
 *
 * Expired batches are excluded — they must not be sold. Batches with no expiry
 * date sort last, so dated stock moves before undated stock.
 *
 * If the batches cannot cover `requiredQty`, the returned allocations sum to
 * less than it. **The caller must detect that and refuse the sale** — this
 * function does not throw.
 */
export function allocateBatchesFEFO(
  requiredQty: number,
  availableBatches: BatchInfo[],
): BatchAllocation[] {
  if (requiredQty <= 0 || availableBatches.length === 0) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const valid = availableBatches.filter(
    (b) => !b.expiryDate || b.expiryDate.getTime() >= today.getTime(),
  );

  const sorted = [...valid].sort((a, b) => {
    if (!a.expiryDate && !b.expiryDate) return 0;
    if (!a.expiryDate) return 1;  // undated sorts after dated
    if (!b.expiryDate) return -1;
    return a.expiryDate.getTime() - b.expiryDate.getTime();
  });

  const allocations: BatchAllocation[] = [];
  let remaining = requiredQty;

  for (const batch of sorted) {
    if (remaining <= 0) break;
    if (batch.quantity <= 0) continue;

    const take = Math.min(batch.quantity, remaining);
    allocations.push({ batchId: batch.id, allocatedQty: take });
    remaining -= take;
  }

  return allocations;
}

/** Total units an allocation set covers. */
export function totalAllocated(allocations: BatchAllocation[]): number {
  return allocations.reduce((sum, a) => sum + a.allocatedQty, 0);
}

/**
 * How much of each batch a sale still holds: what it drew out, minus what has
 * already been given back by earlier credit notes or a void reversal.
 *
 * Without this netting, two partial returns against one invoice would each plan
 * against the *original* draw and put the same units back twice, inflating batch
 * stock. Order is preserved so {@link planBatchRestore} still unwinds in reverse.
 */
export function netRemainingDraw(
  drawn: BatchAllocation[],
  alreadyRestored: BatchAllocation[],
): BatchAllocation[] {
  const credit = new Map<string, number>();
  for (const r of alreadyRestored) {
    credit.set(r.batchId, (credit.get(r.batchId) ?? 0) + r.allocatedQty);
  }

  const out: BatchAllocation[] = [];
  for (const d of drawn) {
    const back = credit.get(d.batchId) ?? 0;
    const remaining = d.allocatedQty - Math.min(back, d.allocatedQty);
    credit.set(d.batchId, back - Math.min(back, d.allocatedQty));
    if (remaining > 0) out.push({ batchId: d.batchId, allocatedQty: remaining });
  }
  return out;
}

export interface BatchRestorePlan {
  /** Units to put back, per batch. */
  allocations: BatchAllocation[];
  /**
   * Units that could not be attributed to any batch — the original sale was
   * product-level (untagged), or predates FEFO. Restore these to
   * `product.currentStock` only, with an untagged movement.
   */
  untagged: number;
}

/**
 * Plan how to put `qtyToRestore` units back, given how the original sale drew
 * them out (`originalAllocations`, i.e. the batch-tagged OUT movements of the
 * invoice for one product).
 *
 * Returns stock to the batches it came from, last-drawn-first, so a partial
 * return unwinds the sale in reverse. Without this, `batch.quantity` only ever
 * decreases: a returned or voided unit lands in `product.currentStock` while the
 * batch stays short, and the two projections drift apart permanently.
 *
 * Restoring more than was drawn (over-return, or a sale that predates batch
 * tagging) yields the surplus as `untagged` rather than inventing batch stock.
 */
export function planBatchRestore(
  originalAllocations: BatchAllocation[],
  qtyToRestore: number,
): BatchRestorePlan {
  if (qtyToRestore <= 0) return { allocations: [], untagged: 0 };

  const allocations: BatchAllocation[] = [];
  let remaining = qtyToRestore;

  // Reverse order: the last batch drawn from is the first refilled.
  for (let i = originalAllocations.length - 1; i >= 0 && remaining > 0; i -= 1) {
    const drawn = originalAllocations[i];
    if (drawn.allocatedQty <= 0) continue;
    const give = Math.min(drawn.allocatedQty, remaining);
    allocations.push({ batchId: drawn.batchId, allocatedQty: give });
    remaining -= give;
  }

  return { allocations, untagged: remaining };
}
